/**
 * Interfaz base para estrategias de creación de comprobantes.
 * Todas las estrategias concretas deben implementar estos métodos.
 * 
 * @abstract
 * @class ReceiptStrategy
 */
class ReceiptStrategy {
    /**
     * Retorna el componente de formulario específico para esta estrategia.
     * 
     * @abstract
     * @returns {React.Component} Componente de formulario
     * @throws {Error} Si no está implementado
     */
    getFormComponent() {
        throw new Error('getFormComponent() debe ser implementado por la estrategia concreta');
    }

    /**
     * Valida los datos del formulario antes de enviar.
     * 
     * @abstract
     * @param {Object} data - Datos del formulario a validar
     * @returns {Object} { isValid: boolean, errors: Object }
     * @throws {Error} Si no está implementado
     */
    validate(data) {
        throw new Error('validate() debe ser implementado por la estrategia concreta');
    }

    /**
     * Envía los datos al backend.
     * 
     * @abstract
     * @param {Object} data - Datos a enviar
     * @param {Object} context - Contexto (uid, cid, profile, etc.)
     * @returns {Promise<Object>} Respuesta del servidor
     * @throws {Error} Si no está implementado
     */
    async submit(data, context) {
        throw new Error('submit() debe ser implementado por la estrategia concreta');
    }

    /**
     * Genera el contenido HTML para impresión.
     * 
     * @abstract
     * @param {Object} data - Datos del comprobante
     * @param {Object} profile - Datos de la empresa
     * @returns {string} HTML para imprimir
     * @throws {Error} Si no está implementado
     */
    generatePrintContent(data, profile) {
        throw new Error('generatePrintContent() debe ser implementado por la estrategia concreta');
    }

    /**
     * Retorna el estado inicial del formulario.
     * 
     * @abstract
     * @returns {Object} Estado inicial
     * @throws {Error} Si no está implementado
     */
    getInitialState() {
        throw new Error('getInitialState() debe ser implementado por la estrategia concreta');
    }

    /**
     * Retorna el título del botón de envío.
     * 
     * @abstract
     * @returns {string} Título del botón
     * @throws {Error} Si no está implementado
     */
    getSubmitButtonTitle() {
        throw new Error('getSubmitButtonTitle() debe ser implementado por la estrategia concreta');
    }

    /**
     * Retorna el nombre del evento a disparar después de crear.
     * 
     * @abstract
     * @returns {string} Nombre del evento
     * @throws {Error} Si no está implementado
     */
    getUpdateEventName() {
        throw new Error('getUpdateEventName() debe ser implementado por la estrategia concreta');
    }
}

export default ReceiptStrategy;
